import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../animations/variants';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { filterInput } from '../utils/inputSanitization';

// Theme color for API Tokens page
const accentColor = '#F39C12';

// Reusable input styles
const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: accentColor },
    '&.Mui-focused fieldset': { borderColor: accentColor },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: accentColor },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Metropolis, sans-serif',
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    border: '1px solid #E5E5E5',
    backgroundColor: '#FFFFFF',
  },
};

// Consistent button styles
const buttonStyles = {
  primary: (color, hoverColor) => ({
    backgroundColor: color,
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': { backgroundColor: hoverColor },
    '&:disabled': {
      backgroundColor: '#E5E5E5',
      color: '#999999',
    },
  }),
  outlined: (color, hoverBgColor) => ({
    borderColor: color,
    color: color,
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': {
      borderColor: color,
      backgroundColor: hoverBgColor,
    },
  }),
  cancel: {
    borderColor: '#E5E5E5',
    color: '#666666',
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': {
      borderColor: '#999999',
      backgroundColor: '#F9FBFD',
    },
  },
  danger: {
    backgroundColor: '#E74C3C',
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '6px',
    px: 3,
    '&:hover': { backgroundColor: '#C0392B' },
  },
};

const APITokens = () => {
  const [staticTokens, setStaticTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [showCreateTokenDialog, setShowCreateTokenDialog] = useState(false);
  const [showTokenSecretDialog, setShowTokenSecretDialog] = useState(false);
  const [newTokenSecret, setNewTokenSecret] = useState(null);
  const [tokenForm, setTokenForm] = useState({ description: '' });
  const [creatingToken, setCreatingToken] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState(null);

  useEffect(() => {
    fetchStaticTokens();
  }, []);

  const fetchStaticTokens = async () => {
    setLoadingTokens(true);
    try {
      const response = await apiService.listStaticTokens();
      if (response.success) {
        const tokens = response.data?.tokens || response.data || [];
        setStaticTokens(Array.isArray(tokens) ? tokens : []);
      }
    } catch (err) {
      console.error('Failed to fetch static tokens:', err);
      setStaticTokens([]);
    } finally {
      setLoadingTokens(false);
    }
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();

    if (!tokenForm.description || tokenForm.description.trim() === '') {
      toast.error('Please provide a description for the token');
      return;
    }

    setCreatingToken(true);

    try {
      const response = await apiService.createStaticToken(tokenForm.description);

      if (response.success && response.data) {
        toast.success('Static token created successfully');
        setNewTokenSecret(response.data);
        setShowCreateTokenDialog(false);
        setShowTokenSecretDialog(true);
        setTokenForm({ description: '' });
        await fetchStaticTokens();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to create static token');
    } finally {
      setCreatingToken(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!tokenToRevoke) return;

    try {
      const response = await apiService.revokeStaticToken(tokenToRevoke.id);

      if (response.success) {
        toast.success('Static token revoked successfully');
        setShowRevokeConfirm(false);
        setTokenToRevoke(null);
        await fetchStaticTokens();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to revoke static token');
    }
  };

  const copyTokenToClipboard = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success('Token copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy token to clipboard');
    }
  };

  const maskToken = (token) => {
    if (!token || token.length < 12) return token;
    return `${token.substring(0, 12)}${'*'.repeat(token.length - 12)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const cardStyle = {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E5E5',
    borderRadius: '8px',
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              backgroundColor: accentColor,
              width: 48,
              height: 48,
            }}
          >
            <VpnKeyIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.75rem',
                color: '#253E66',
              }}
            >
              API Tokens
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.875rem',
                color: '#666666',
                fontWeight: 500,
              }}
            >
              Generate and manage tokens for Terraform provider authentication
            </Typography>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Main Content */}
      <motion.div variants={sectionVariants}>
      <Card elevation={0} sx={cardStyle}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: accentColor,
                  width: 40,
                  height: 40,
                }}
              >
                <VpnKeyIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: '#253E66',
                }}
              >
                Static API Tokens
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<VpnKeyIcon />}
              onClick={() => setShowCreateTokenDialog(true)}
              sx={buttonStyles.primary(accentColor, '#E67E22')}
            >
              Create Token
            </Button>
          </Box>

          <Divider sx={{ mb: 2, borderColor: '#E5E5E5' }} />

          {loadingTokens ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: accentColor }} />
            </Box>
          ) : staticTokens.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <VpnKeyIcon sx={{ fontSize: 48, color: '#E5E5E5', mb: 1 }} />
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                }}
              >
                No API tokens created yet. Create your first token to get started.
              </Typography>
            </Box>
          ) : (
            <List sx={{ pt: 0 }}>
              {staticTokens.map((token, index) => (
                <ListItem
                  key={token.id}
                  sx={{
                    '@keyframes fadeInItem': {
                      from: { opacity: 0, transform: 'translateY(8px)' },
                      to: { opacity: 1, transform: 'translateY(0)' },
                    },
                    animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                    backgroundColor: '#F9FBFD',
                    borderRadius: '6px',
                    mb: 1,
                    border: '1px solid #E5E5E5',
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                            color: '#253E66',
                          }}
                        >
                          {token.description}
                        </Typography>
                        <Chip
                          label={maskToken(token.tokenPrefix + '...')}
                          size="small"
                          sx={{
                            bgcolor: '#FEF9E7',
                            color: accentColor,
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontSize: '0.75rem',
                          color: '#999999',
                        }}
                      >
                        Created: {formatDate(token.createdAt)}
                        {token.lastUsedAt && ` • Last used: ${formatDate(token.lastUsedAt)}`}
                      </Typography>
                    }
                  />
                  <IconButton
                    onClick={() => {
                      setTokenToRevoke(token);
                      setShowRevokeConfirm(true);
                    }}
                    sx={{
                      color: '#E74C3C',
                      '&:hover': {
                        backgroundColor: '#FDF2F2',
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
      </motion.div>

      {/* Create Token Dialog */}
      <Dialog
        open={showCreateTokenDialog}
        onClose={() => setShowCreateTokenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#FEF9E7',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
          }}
        >
          Create API Token
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.875rem',
              color: '#666666',
              mb: 2,
            }}
          >
            Create a new API token for authenticating your Terraform provider. The token will be shown only once.
          </Typography>
          <TextField
            autoFocus
            label="Token Description"
            fullWidth
            value={tokenForm.description}
            onChange={(e) => setTokenForm({ ...tokenForm, description: filterInput(e.target.value, 'GENERAL_TEXT') })}
            placeholder="e.g., Terraform Provider Token"
            size="small"
            sx={inputStyle}
          />
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => {
              setShowCreateTokenDialog(false);
              setTokenForm({ description: '' });
            }}
            variant="outlined"
            sx={buttonStyles.cancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateToken}
            disabled={creatingToken || !tokenForm.description.trim()}
            variant="contained"
            sx={buttonStyles.primary(accentColor, '#E67E22')}
            startIcon={creatingToken ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <VpnKeyIcon />}
          >
            {creatingToken ? 'Creating...' : 'Create Token'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Token Secret Display Dialog */}
      <Dialog
        open={showTokenSecretDialog}
        onClose={() => {
          setShowTokenSecretDialog(false);
          setNewTokenSecret(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#E8F8F0',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
          }}
        >
          API Token Created
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              backgroundColor: '#FEF9E7',
              border: '1px solid #F39C1230',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: accentColor },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Save this token now! You won't be able to see it again.
            </Typography>
          </Alert>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.875rem',
              color: '#666666',
              mb: 2,
            }}
          >
            Use this token in your Terraform provider configuration's X-API-Token header.
          </Typography>
          <Box
            sx={{
              backgroundColor: '#1E293B',
              p: 2,
              borderRadius: '6px',
              border: '1px solid #334155',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              color: accentColor,
              fontSize: '0.875rem',
              mb: 2,
            }}
          >
            {newTokenSecret?.token}
          </Box>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => copyTokenToClipboard(newTokenSecret?.token)}
            sx={buttonStyles.outlined(accentColor, '#FEF9E7')}
          >
            Copy to Clipboard
          </Button>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setShowTokenSecretDialog(false);
              setNewTokenSecret(null);
            }}
            variant="contained"
            sx={buttonStyles.primary('#27AE60', '#219A52')}
          >
            I've Saved the Token
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Token Confirmation Dialog */}
      <Dialog
        open={showRevokeConfirm}
        onClose={() => {
          setShowRevokeConfirm(false);
          setTokenToRevoke(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#FDF2F2',
            borderBottom: '1px solid #E5E5E5',
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#E74C3C',
          }}
        >
          Revoke API Token?
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: '#FDF2F2',
              border: '1px solid #E74C3C30',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#E74C3C' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              This action cannot be undone!
            </Typography>
          </Alert>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.875rem',
              color: '#666666',
              mb: 1,
            }}
          >
            Are you sure you want to revoke the token: <strong>{tokenToRevoke?.description}</strong>?
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontSize: '0.8rem',
              color: '#999999',
            }}
          >
            Any applications or tools using this token will immediately lose access.
          </Typography>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => {
              setShowRevokeConfirm(false);
              setTokenToRevoke(null);
            }}
            variant="outlined"
            sx={buttonStyles.cancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRevokeToken}
            variant="contained"
            sx={buttonStyles.danger}
            startIcon={<DeleteIcon />}
          >
            Revoke Token
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default APITokens;
